/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineChartPie,
  HiOutlineCollection,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineFlag,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineViewGrid,
} from "react-icons/hi";

import ListPage from "../../../layout/ListPage";
import type { Action, Column } from "../../../ui";
import { useAdminContext } from "../../../context/admin/AdminContext";
import { useCountryContext } from "../../../context/CountryContext";
import {
  addOneCountry,
  cleanOneLevel,
  getOneRegion,
} from "../../../lib/breadCrumb/BreadCrumb";
import {
  pushCountry,
  pushSingleCountryDashboard,
} from "../../../lib/sysNavigation/SysNavigation";

type CountryRow = {
  country_id: number | string;
  name?: string;
  slug?: string;
  status?: number;
  continent_region_id?: number | string;
  [key: string]: unknown;
};

const addRoute = "/countries/add";
const editRoute = "/countries/edit";
const dashboardRoute = "/country/dashboard";
const contentRoute = "/dosee/data";
const levelsRoute = "/levels/first";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { sysAdmin } = useAdminContext();
  const isSuperAdmin = ["super_admin", "admin"].includes((sysAdmin as any)?.role);
  const {
    countries,
    count,
    fetchCountries,
    deleteCountry,
    updateCountry,
    updateCountryState,
  } = useCountryContext() as any;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [query, setQuery] = useState("");

  const regionFilterId = useMemo(() => {
    const r = getOneRegion();
    return r.length > 0 ? r[0]?.id : null;
  }, []);

  useEffect(() => {
    if (!countries) fetchCountries(page, pageSize);
  }, [countries]);

  const rows = useMemo<CountryRow[] | null>(() => {
    if (!Array.isArray(countries)) return null;
    let base = [...(countries as CountryRow[])].sort((a, b) =>
      (a?.name ?? "").localeCompare(b?.name ?? ""),
    );
    if (regionFilterId) {
      base = base.filter((c) => c?.continent_region_id === regionFilterId);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      base = base.filter((c) =>
        String(c.name ?? "").toLowerCase().includes(q),
      );
    }
    return base;
  }, [countries, query, regionFilterId]);

  const toggleVisibility = async (row: CountryRow) => {
    const { country_id, ...rest } = row;
    const payload = { ...rest, status: row.status === 0 ? 1 : 0 };
    const { status } = await updateCountry(country_id, payload);
    if (status === 200) await fetchCountries(1, pageSize);
  };

  const deleteOne = async (row: CountryRow) => {
    const { status } = await deleteCountry(row.country_id);
    if (status === 200 && Array.isArray(countries)) {
      updateCountryState(
        (countries as CountryRow[]).filter(
          (c) => c.country_id !== row.country_id,
        ),
      );
    }
  };

  const openContent = (row: CountryRow) => {
    addOneCountry({
      id: row.country_id,
      name: row.name,
      slug: row.slug,
      route: "/countries",
      data: row,
    });
    cleanOneLevel();
    pushCountry({
      country: {
        id: row.country_id,
        name: row.name,
        slug: row.slug,
        route: "/countries",
      },
    });
    navigate(contentRoute);
  };

  const openLevels = (row: CountryRow) => {
    addOneCountry({
      id: row.country_id,
      name: row.name,
      slug: row.slug,
      route: "/countries",
      data: row,
    });
    pushCountry({
      country: {
        id: row.country_id,
        name: row.name,
        slug: row.slug,
        route: "/countries",
      },
    });
    navigate(levelsRoute);
  };

  const columns: Column<CountryRow>[] = [
    {
      header: "Country",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <HiOutlineFlag className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">
              {row.name ?? "—"}
            </p>
            <p className="truncate text-[11px] text-gray-500">
              {row.slug ?? ""}
            </p>
          </div>
        </div>
      ),
    },
    {
      header: "Status",
      cell: (row) =>
        row.status === 1 ? (
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
            Visible
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-medium text-red-700 ring-1 ring-inset ring-red-200">
            Hidden
          </span>
        ),
    },
  ];

  const rowActions = (row: CountryRow): Action[] => {
    const actions: Action[] = [];

    if (isSuperAdmin) {
      actions.push({
        label: row.status === 1 ? "Hide" : "Show",
        icon:
          row.status === 1 ? (
            <HiOutlineEyeOff className="h-3.5 w-3.5" />
          ) : (
            <HiOutlineEye className="h-3.5 w-3.5" />
          ),
        onClick: () => toggleVisibility(row),
        confirm: {
          title: row.status === 1 ? "Hide this country?" : "Make visible?",
          description:
            row.status === 1
              ? `This hides ${row.name} from public views.`
              : `This shows ${row.name} on public views.`,
          confirmLabel: row.status === 1 ? "Hide" : "Show",
        },
      });
    }

    actions.push({
      label: "Dashboard",
      icon: <HiOutlineChartPie className="h-3.5 w-3.5" />,
      onClick: () => {
        pushSingleCountryDashboard(row.name);
        navigate(`${dashboardRoute}/${row.country_id}`);
      },
    });

    actions.push({
      label: "Content",
      icon: <HiOutlineCollection className="h-3.5 w-3.5" />,
      onClick: () => openContent(row),
    });

    if (isSuperAdmin) {
      actions.push({
        label: "Levels",
        icon: <HiOutlineViewGrid className="h-3.5 w-3.5" />,
        onClick: () => openLevels(row),
      });
      actions.push({
        label: "Edit",
        icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
        onClick: () => navigate(`${editRoute}/${row.country_id}`),
      });
      actions.push({
        label: "Delete",
        variant: "destructive",
        icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
        onClick: () => deleteOne(row),
        confirm: {
          title: `Delete ${row.name ?? "this country"}?`,
          description: "This action cannot be undone.",
          confirmLabel: "Delete",
        },
      });
    }

    return actions;
  };

  const pageActions: Action[] = isSuperAdmin
    ? [{ label: "Add country", variant: "primary", href: addRoute }]
    : [];

  return (
    <ListPage<CountryRow>
      title="Countries"
      subtitle="Country records managed across the African continent."
      icon={<HiOutlineFlag className="h-5 w-5" />}
      actions={pageActions}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.country_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search countries…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchCountries(p, ps);
        },
      }}
      empty="No countries yet."
    />
  );
};

export default Overview;
